import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/PageHeader';
import { Card, SectionTitle } from '@/components/Card';
import { Badge } from '@/components/Badge';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Spinner } from '@/components/LoadingScreen';
import { Icon, type IconName } from '@/components/Icon';
import { useSelection, COMPANY_SCOPE } from '@/app/SelectionContext';
import { useOperationalAccess } from '@/app/useOperationalAccess';
import { OperationalKpi } from '@/features/operational/OperationalKpi';
import { formatByCurrency, formatDate, hasAmount } from '@/features/operational/money';
import { operationalOverviewService, apService } from '@/services/operational.service';
import type { OperationalModule } from '@/types/operational';
import { v } from '@/theme/tokens';

/**
 * The landing page of «Операционный контроль».
 *
 * A module the caller has no grant for arrives from the server as `null`, and
 * this page renders nothing for it — no card, no zero. A person without AP
 * access is not told that the company owes nothing.
 */
export function OperationalOverviewPage() {
  const access = useOperationalAccess();
  const { scope } = useSelection();
  const unitId = scope === COMPANY_SCOPE ? undefined : scope;

  const overview = useQuery({
    queryKey: ['operational', 'overview', unitId ?? 'company'],
    queryFn: ({ signal }) => operationalOverviewService.read(unitId, signal),
    enabled: !access.loading,
  });

  const balances = useQuery({
    queryKey: ['operational', 'ap', 'balances'],
    queryFn: ({ signal }) => apService.balances(signal),
    enabled: access.can('SUPPLIERS_AP'),
  });

  if (access.loading || overview.isPending) return <Spinner />;

  if (!access.hasAny) {
    return (
      <>
        <PageHeader title="Операционный контроль" />
        <EmptyState
          icon="lock"
          title="Раздел недоступен"
          description="Дополнительный доступ к операционным модулям выдаёт владелец в разделе «Настройки → Пользователи и роли»."
        />
      </>
    );
  }

  if (overview.error) {
    return (
      <>
        <PageHeader title="Операционный контроль" />
        <ErrorState
          message={overview.error instanceof Error ? overview.error.message : 'Ошибка загрузки'}
          onRetry={() => void overview.refetch()}
        />
      </>
    );
  }

  const data = overview.data;
  const inventory = data?.inventory ?? null;
  const ap = data?.accounts_payable ?? null;

  return (
    <>
      <PageHeader
        title="Операционный контроль"
        subtitle={data?.business_unit_name ?? 'Вся компания'}
      />

      <div
        style={{
          display: 'grid',
          // Cards flow onto as many rows as the screen needs; never a
          // fixed column count that would force the page wider than a phone.
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(210px, 100%), 1fr))',
          gap: 14,
          marginBottom: 22,
        }}
      >
        {inventory && (
          <>
            <OperationalKpi
              label="Стоимость запасов"
              values={formatByCurrency(inventory.stock_value_by_currency)}
              caption={
                inventory.last_inventory_date
                  ? `последняя инвентаризация ${formatDate(inventory.last_inventory_date)}`
                  : 'инвентаризаций ещё не было'
              }
            />
            <OperationalKpi
              label="Изменение запасов"
              values={formatByCurrency(inventory.change_by_currency, { signed: true })}
              caption="к прошлой инвентаризации"
              tone={hasAmount(inventory.change_by_currency) ? 'neutral' : 'neutral'}
            />
          </>
        )}

        {ap && (
          <>
            <OperationalKpi
              label="Долг поставщикам"
              values={formatByCurrency(ap.outstanding_by_currency)}
              caption={`поставщиков с долгом: ${ap.suppliers_with_debt}`}
            />
            <OperationalKpi
              label="Просрочено"
              values={formatByCurrency(ap.overdue_by_currency)}
              caption={`счетов: ${ap.overdue_invoice_count}`}
              tone={hasAmount(ap.overdue_by_currency) ? 'negative' : 'neutral'}
            />
            <OperationalKpi
              label={`К оплате ${ap.due_soon_days} дней`}
              values={formatByCurrency(ap.due_soon_by_currency)}
              caption="по сроку оплаты"
              tone={hasAmount(ap.due_soon_by_currency) ? 'warning' : 'neutral'}
            />
          </>
        )}
      </div>

      {ap && hasAmount(ap.unallocated_by_currency) && (
        <Card style={{ marginBottom: 22 }}>
          <div style={{ fontSize: 13, color: v('ink-sub') }}>
            Есть оплаты, не разнесённые по счетам:{' '}
            <strong>{formatByCurrency(ap.unallocated_by_currency).join(' · ')}</strong>. Пока они
            не разнесены, долг по счетам показывается полностью.
          </div>
        </Card>
      )}

      {ap && (
        <Card style={{ marginBottom: 22 }}>
          <SectionTitle
            right={
              <Link
                to="/operational/suppliers"
                // A standalone link, so it gets a fingertip's height rather
                // than its 15 px line box (WCAG 2.5.8).
                style={{ display: 'inline-flex', alignItems: 'center', minHeight: 32, fontSize: 12.5 }}
              >
                Все поставщики →
              </Link>
            }
          >
            Долг по поставщикам
          </SectionTitle>
          {balances.isPending ? (
            <Spinner />
          ) : (balances.data ?? []).length === 0 ? (
            <div style={{ fontSize: 13, color: v('ink-muted') }}>Задолженности нет.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: v('ink-muted'), fontSize: 11.5 }}>
                    <th style={{ padding: '6px 8px 6px 0' }}>Поставщик</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>Текущий долг</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>Просрочено</th>
                    <th style={{ padding: '6px 8px' }}>Ближайший срок</th>
                    <th style={{ padding: '6px 0 6px 8px' }}>Объект</th>
                  </tr>
                </thead>
                <tbody>
                  {(balances.data ?? []).slice(0, 8).map((row) => (
                    <tr key={row.supplier_id} style={{ borderTop: `1px solid ${v('line-soft')}` }}>
                      <td style={{ padding: '8px 8px 8px 0' }}>
                        <Link
                          to={`/operational/suppliers?supplier=${row.supplier_id}`}
                          style={{ color: v('ink'), fontWeight: 600 }}
                        >
                          {row.supplier_name}
                        </Link>
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {formatByCurrency(row.outstanding_by_currency).join(' · ')}
                      </td>
                      <td
                        style={{
                          padding: '8px',
                          textAlign: 'right',
                          whiteSpace: 'nowrap',
                          color: hasAmount(row.overdue_by_currency) ? v('negative') : v('ink-muted'),
                        }}
                      >
                        {hasAmount(row.overdue_by_currency)
                          ? formatByCurrency(row.overdue_by_currency).join(' · ')
                          : '—'}
                      </td>
                      <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>
                        {formatDate(row.earliest_due_date)}
                      </td>
                      <td style={{ padding: '8px 0 8px 8px', color: v('ink-muted') }}>
                        {row.business_unit_names.join(', ') || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      <SectionLinks
        access={access}
        pendingInventory={inventory?.documents_awaiting_review ?? 0}
        pendingDocuments={data?.documents_pending ?? 0}
      />
    </>
  );
}

/**
 * In-page navigation to the section's pages.
 *
 * Exists mostly for the phone, where the bottom bar carries one Operational
 * entry rather than four — this is how the other pages are reached there.
 */
function SectionLinks({
  access,
  pendingInventory,
  pendingDocuments,
}: {
  access: ReturnType<typeof useOperationalAccess>;
  pendingInventory: number;
  pendingDocuments: number;
}) {
  type SectionLink = {
    to: string;
    label: string;
    icon: IconName;
    module: OperationalModule;
    badge?: number;
  };

  const links: SectionLink[] = ([
    {
      to: '/operational/inventory',
      label: 'Инвентаризация',
      icon: 'clipboard',
      module: 'INVENTORY',
      badge: pendingInventory,
    },
    {
      to: '/operational/suppliers',
      label: 'Поставщики и долги',
      icon: 'truck',
      module: 'SUPPLIERS_AP',
    },
    {
      to: '/operational/documents',
      label: 'Документы',
      icon: 'scan',
      module: 'DOCUMENTS_OCR',
      badge: pendingDocuments,
    },
  ] satisfies SectionLink[]).filter((link) => access.can(link.module));

  if (links.length === 0) return null;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))',
        gap: 14,
      }}
    >
      {links.map((link) => (
        <Link key={link.to} to={link.to} style={{ textDecoration: 'none' }}>
          <Card padding={16}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
              <span style={{ color: v('accent'), display: 'flex' }}>
                <Icon name={link.icon} size={19} />
              </span>
              <span style={{ flex: 1, minWidth: 0, fontWeight: 600, fontSize: 13.5, color: v('ink') }}>
                {link.label}
              </span>
              {link.badge ? <Badge tone="warning">{link.badge}</Badge> : null}
            </div>
          </Card>
        </Link>
      ))}
    </div>
  );
}
